import { useState } from "react"
import { Code2, Copy, Send, Check } from "lucide-react"
import whatsappIcon from "@/assets/whatsapp.svg"
import gmailIcon from "@/assets/gmail.svg"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { EMBED_IFRAME_ALLOW } from '@/features/embed/embed-bridge'

interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  meetingUrl: string
  sipAddress: string
}

const SHARE_ACTIONS = [
  { id: "embed", label: "Embed", icon: Code2 },
  { id: "whatsapp", label: "WhatsApp", icon: "whatsapp" },
  { id: "mail", label: "Mail", icon: gmailIcon },
  { id: "email", label: "Email", icon: Send },
  { id: "copy", label: "Copy", icon: Copy },
] as const

export default function ShareDialog({ open, onOpenChange, meetingUrl, sipAddress }: ShareDialogProps) {
  const t = useTranslation()
  const [copied, setCopied] = useState(false)

  const getActionLabel = (id: (typeof SHARE_ACTIONS)[number]["id"]) => {
    if (id === "embed") return t("meetings.embed")
    if (id === "whatsapp") return t("meetings.whatsApp")
    if (id === "mail") return t("common.mail")
    if (id === "email") return t("common.email")
    return t("common.copy")
  }

  const showCopied = (title: string, description: string) => {
    setCopied(true)
    toast.success({ title, description })
    setTimeout(() => setCopied(false), 1400)
  }

  const handleAction = (id: (typeof SHARE_ACTIONS)[number]["id"]) => {
    if (id === "embed") {
      const embedCode = `<iframe src="${meetingUrl}" width="100%" height="540" frameborder="0" allow="${EMBED_IFRAME_ALLOW}"></iframe>`
      navigator.clipboard.writeText(embedCode)
      showCopied(t("meetings.embedCodeCopied"), t("meetings.pasteEmbedCode"))
      return
    }

    if (id === "copy") {
      navigator.clipboard.writeText(meetingUrl)
      showCopied(t("common.copied"), t("meetings.meetingLinkCopied"))
      return
    }

    if (id === "whatsapp") {
      window.open(`https://wa.me/?text=${encodeURIComponent(`Join my meeting: ${meetingUrl}`)}`)
      return
    }

    if (id === "mail" || id === "email") {
      window.open(`mailto:?subject=${encodeURIComponent(t("meetings.meetingInvite"))}&body=${encodeURIComponent(`${t("meetings.joinMeeting")}: ${meetingUrl}`)}`)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="sm">
      <DialogContent className="max-w-xl p-0 overflow-hidden">
        <DialogHeader className="px-8 py-6 border-b border-border">
          <DialogTitle className="text-xl leading-none font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("common.share")}</DialogTitle>
        </DialogHeader>

        <div className="px-8 pb-8 pt-2 space-y-4">
          {/* <p className="text-md leading-none text-slate-800 dark:text-slate-100">{sipAddress}</p> */}

          <div className="grid grid-cols-4 sm:grid-cols-5 gap-x-5 py-2">
            {SHARE_ACTIONS.map((action) => (
              <button
                key={action.id}
                onClick={() => handleAction(action.id)}
                className="flex flex-col items-center gap-2 group"
              >
                <span className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:bg-slate-200 dark:group-hover:bg-slate-700 transition-colors">
                  {action.id === 'whatsapp' ? (
                    <img src={whatsappIcon} alt="WhatsApp" className="w-full h-full" />
                  ) : action.id === 'mail' ? (
                    <img src={gmailIcon} alt="Mail" className="w-full h-full" />
                  ) : (
                    <action.icon className="w-5 h-5 text-slate-700 dark:text-slate-200" />
                  )}
                </span>
                <span className="text-sm text-slate-700 dark:text-slate-200">{getActionLabel(action.id)}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-4 border border-border rounded-xl px-2 py-2 bg-slate-50/70 dark:bg-slate-900/40">
            <p className="text-md text-sky-600 dark:text-sky-400 truncate">{meetingUrl}</p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(meetingUrl)
                showCopied(t("common.copied"), t("meetings.meetingLinkCopied"))
              }}
              className="w-8 h-8 rounded-lg transition-colors shrink-0 flex items-center justify-center"
              title={copied ? t("common.copied") : t("meetings.copyLink")}
            >
              {copied ? <Check className="w-4 h-4 text-emerald-500 animate-in zoom-in-75 duration-200" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
